import type { PolyNoteEvent } from "../hooks/usePolyPitchDetector";
import { heardMidiMatchesExpected } from "./pitchMatch";

export interface GuidedPolyMatchResult {
  heardMidis: number[];
  matchedNoteIndices: number[];
  matchedExpectedMidis: number[];
  coverage: number;
}

/**
 * Asigna eventos de Basic Pitch a las notas esperadas del pentagrama (1:1 greedy).
 */
export function matchGuidedPolyphony(
  polyEvents: PolyNoteEvent[],
  expectedMidis: number[],
  noteIndices: number[]
): GuidedPolyMatchResult {
  if (expectedMidis.length === 0 || noteIndices.length !== expectedMidis.length) {
    return { heardMidis: [], matchedNoteIndices: [], matchedExpectedMidis: [], coverage: 0 };
  }

  const heardMidis = polyEvents.map((e) => Math.round(e.pitch));
  const usedEvent = new Set<number>();
  const matchedNoteIndices: number[] = [];
  const matchedExpectedMidis: number[] = [];

  for (let i = 0; i < expectedMidis.length; i++) {
    const expected = expectedMidis[i];
    const noteIndex = noteIndices[i];
    let bestJ = -1;
    let bestDist = Infinity;

    for (let j = 0; j < polyEvents.length; j++) {
      if (usedEvent.has(j)) continue;
      const ev = polyEvents[j];
      if (!heardMidiMatchesExpected(ev.pitch, expected)) continue;
      const dist = Math.min(
        Math.abs(Math.round(ev.pitch) - Math.round(expected)),
        Math.abs((Math.round(ev.pitch) % 12) - (Math.round(expected) % 12))
      );
      if (dist < bestDist || (dist === bestDist && ev.confidence > (polyEvents[bestJ]?.confidence ?? 0))) {
        bestDist = dist;
        bestJ = j;
      }
    }

    if (bestJ >= 0) {
      usedEvent.add(bestJ);
      matchedNoteIndices.push(noteIndex);
      matchedExpectedMidis.push(expected);
    }
  }

  const coverage =
    expectedMidis.length > 0 ? matchedNoteIndices.length / expectedMidis.length : 0;

  return {
    heardMidis,
    matchedNoteIndices,
    matchedExpectedMidis,
    coverage,
  };
}

export function applyGuidedPolyToNoteScores(
  matchedNoteIndices: number[],
  noteScores: Array<{ noteIndex: number; status: string }>,
  songMs: number,
  windowEndMs: number
): boolean {
  let changed = false;
  const isLate = songMs > windowEndMs;
  for (const idx of matchedNoteIndices) {
    const s = noteScores.find((n) => n.noteIndex === idx);
    if (!s || s.status !== "pending") continue;
    s.status = isLate ? "late" : "hit";
    changed = true;
  }
  return changed;
}
